<template>
  <div class="fw-root">

    <!-- ═══ INVALID / LOADING ═══════════════════════════════════════ -->
    <div v-if="status === 'loading'" class="fw-center">
      <div class="fw-spinner"></div>
      <p class="fw-muted">{{ t('fwLoading') }}</p>
    </div>
    <div v-else-if="status === 'invalid'" class="fw-center">
      <div class="fw-card fw-error-card">
        <div style="font-size:48px">🔒</div>
        <h2>{{ t('fwInvalidTitle') }}</h2>
        <p>{{ t('fwInvalidMsg') }}</p>
      </div>
    </div>

    <!-- ═══ TUTORIAL ════════════════════════════════════════════════ -->
    <div v-else-if="!tutoDone" class="fw-tuto-screen">
      <div class="fw-tuto-header">
        <div class="fw-tuto-emoji">🗺️</div>
        <h1 class="fw-tuto-title">{{ gameName }}</h1>
        <p class="fw-tuto-sub">{{ points.length }} {{ t('fwPointsToFill') }}</p>
      </div>

      <div class="fw-tuto-cards">
        <div class="fw-tuto-card">
          <div class="fw-tuto-card-icon">📸</div>
          <div class="fw-tuto-card-title">{{ t('fwTutoPhotoTitle') }}</div>
          <div class="fw-tuto-card-body">{{ t('fwTutoPhotoBody') }}</div>
        </div>
        <div class="fw-tuto-card">
          <div class="fw-tuto-card-icon">📍</div>
          <div class="fw-tuto-card-title">{{ t('fwTutoGpsTitle') }}</div>
          <div class="fw-tuto-card-body">{{ t('fwTutoGpsBody') }}</div>
        </div>
        <div class="fw-tuto-card">
          <div class="fw-tuto-card-icon">💾</div>
          <div class="fw-tuto-card-title">{{ t('fwTutoSaveTitle') }}</div>
          <div class="fw-tuto-card-body">{{ t('fwTutoSaveBody') }}</div>
        </div>
      </div>

      <button class="fw-start-btn" @click="tutoDone = true">{{ t('fwStart') }}</button>
    </div>

    <!-- ═══ POINTS LIST ══════════════════════════════════════════════ -->
    <div v-else class="fw-main">
      <div class="fw-header">
        <div class="fw-header-title">🗺️ {{ gameName }}</div>
        <div class="fw-progress">
          {{ savedCount }} / {{ points.length }} {{ t('fwSavedCount') }}
          <div class="fw-progress-bar">
            <div class="fw-progress-fill" :style="{ width: (savedCount / points.length * 100) + '%' }"></div>
          </div>
        </div>
      </div>

      <div class="fw-points">
        <div v-for="point in points" :key="point.num" class="fw-card" :class="{ 'fw-card--done': point._saved }">

          <div class="fw-card-header">
            <span class="fw-badge">{{ point.num }}</span>
            <span v-if="point._saved" class="fw-done-tag">{{ t('fwDoneTag') }}</span>
            <span v-else class="fw-todo-tag">{{ t('fwTodoTag') }}</span>
          </div>

          <!-- Photo -->
          <div class="fw-section">
            <div class="fw-section-label">{{ t('fwPhotoLabel') }}</div>
            <div class="fw-section-hint">{{ t('fwPhotoHint') }}</div>
            <div v-if="point._preview" class="fw-preview-wrap">
              <img :src="point._preview" class="fw-preview-img" alt="" />
              <button class="fw-remove-btn" @click="removePhoto(point)" :disabled="point._saving">✕</button>
            </div>
            <div class="fw-photo-actions">
              <label class="fw-upload-label">
                {{ point._preview ? t('fwChoosePhoto') : t('fwChoosePhoto') }}
                <input type="file" accept="image/*" capture="environment" style="display:none"
                  @change="e => onPhotoPicked(e, point)" />
              </label>
              <button v-if="point._preview" class="fw-edit-photo-btn" @click="openPhotoEditor(point)">
                {{ t('editPhoto') }}
              </button>
            </div>
          </div>

          <!-- GPS -->
          <div class="fw-section">
            <div class="fw-section-label">{{ t('fwGpsLabel') }}</div>
            <div class="fw-section-hint">{{ t('fwGpsHint') }}</div>
            <div class="fw-gps-row">
              <input v-model="point._lat" class="fw-input" :placeholder="t('fwLatPlaceholder')" inputmode="decimal" />
              <input v-model="point._lng" class="fw-input" :placeholder="t('fwLngPlaceholder')" inputmode="decimal" />
            </div>
            <button class="fw-map-btn" @click="openMapPicker(point)">🗺️ {{ t('fwPickOnMap') }}</button>
          </div>

          <!-- Save -->
          <button class="fw-save-btn"
            :disabled="!point._preview || !point._lat || !point._lng || point._saving"
            @click="savePoint(point)">
            <span v-if="point._saving">{{ t('fwSaving') }}</span>
            <span v-else-if="point._saved">{{ t('fwModify') }}</span>
            <span v-else>{{ t('fwSavePoint') }}</span>
          </button>

          <div v-if="point._error" class="fw-error-msg">❌ {{ point._error }}</div>

        </div>
      </div>

      <div v-if="savedCount === points.length && points.length > 0" class="fw-all-done">
        {{ t('fwAllDone') }}
      </div>
    </div>

    <!-- Photo Editor -->
    <PhotoEditor
      v-if="photoEditor.open"
      :src="photoEditor.src"
      :lang="adminLang"
      @save="onPhotoEditorSave"
      @cancel="photoEditor.open = false"
    />

    <!-- Image Cropper -->
    <ImageCropper
      v-if="imageCropper.show"
      :src="imageCropper.src"
      :lang="adminLang"
      @save="onCropperSave"
      @cancel="onCropperCancel"
    />

    <!-- Map Picker Modal -->
    <div v-if="mapPicker.open" class="fw-map-overlay" @click.self="mapPicker.open = false">
      <div class="fw-map-modal">
        <div class="fw-map-header">
          <span>{{ t('mapPickerHint') }}</span>
          <button class="fw-map-close" @click="mapPicker.open = false">✕</button>
        </div>
        <div id="fw-map-container" style="width:100%;height:55vh;"></div>
        <div class="fw-map-footer">
          <span v-if="mapPicker.selected" class="fw-map-coords">
            {{ mapPicker.selected.lat }}, {{ mapPicker.selected.lng }}
          </span>
          <span v-else class="fw-map-hint-text">{{ t('mapPickerHint') }}</span>
          <button class="fw-map-confirm" :disabled="!mapPicker.selected" @click="applyMapCoords">
            {{ t('confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="toast.show" :class="['fw-toast', toast.type]">{{ toast.text }}</div>

  </div>
</template>

<script>
/* eslint-disable */
import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/storage';
import { adminUI } from '../i18n/adminUI';
import PhotoEditor from './PhotoEditor.vue';
import ImageCropper from './ImageCropper.vue';

export default {
  components: { PhotoEditor, ImageCropper },
  data() {
    return {
      adminLang: localStorage.getItem('adminLang') || 'fr',
      status: 'loading', // loading | invalid | ready
      gameName: '',
      mapCenter: { lat: 32.657, lng: 35.417, zoom: 15 },
      points: [],
      tutoDone: false,
      toast: { show: false, text: '', type: 'success' },
      mapPicker: { open: false, targetPoint: null, selected: null },
      photoEditor: { open: false, src: '', targetPoint: null },
      imageCropper: { show: false, src: '', _resolve: null },
    };
  },

  computed: {
    savedCount() {
      return this.points.filter(p => p._saved).length;
    },
  },

  async mounted() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const game = params.get('game');
    const lang = params.get('lang');
    if (lang && ['fr', 'en', 'he'].includes(lang)) this.adminLang = lang;
    if (!token || !game) { this.status = 'invalid'; return; }

    // Validate token — stored directly in the game's own path
    const db = firebase.database();
    const snap = await db.ref(game + '/fieldToken').once('value');
    const storedToken = snap.val();
    if (!storedToken || storedToken !== token) { this.status = 'invalid'; return; }

    this.gameName = game;

    // Load map center from game settings
    const mpSnap = await db.ref(game + '/mapParameters').once('value');
    const mp = mpSnap.val() || {};
    if (mp.lat && mp.lng) {
      this.mapCenter = { lat: parseFloat(mp.lat), lng: parseFloat(mp.lng), zoom: parseInt(mp.zoom) || 15 };
    } else {
      fetch('https://ip-api.com/json/?fields=lat,lon,status')
        .then(r => r.json())
        .then(d => { if (d.status === 'success') this.mapCenter = { lat: d.lat, lng: d.lon, zoom: 13 }; })
        .catch(() => {});
    }

    // Load point numbers + existing saved data in parallel
    // Load point list + only field-worker-submitted data (not cloned data)
    const [ptsSnap, fwSnap] = await Promise.all([
      db.ref(game + '/he/points').once('value'),
      db.ref(game + '/fieldWorkerData').once('value'),
    ]);

    const ptsVal = ptsSnap.val() || {};
    const fwVal  = fwSnap.val()  || {};

    this.points = Object.keys(ptsVal)
      .map(num => parseInt(num))
      .sort((a, b) => a - b)
      .map(num => {
        const fw = fwVal[num] || {};
        const hasSaved = !!(fw.imageUrl && fw.lat && fw.lng);
        return {
          num,
          _lat:     fw.lat      ? String(fw.lat)      : '',
          _lng:     fw.lng      ? String(fw.lng)      : '',
          _preview: fw.imageUrl || null,
          _file:    null,
          _saving:  false,
          _saved:   hasSaved,
          _error:   '',
        };
      });

    // Skip tutorial if the worker is returning with data they already submitted
    if (this.points.some(p => p._saved)) this.tutoDone = true;

    this.status = 'ready';
  },

  methods: {
    t(key) {
      const lang = adminUI[this.adminLang] || adminUI.fr;
      return lang[key] || adminUI.fr[key] || key;
    },

    onPhotoPicked(event, point) {
      const file = event.target.files[0];
      if (!file) return;
      // Try to extract GPS from EXIF before decoding
      const arrayReader = new FileReader();
      arrayReader.onload = (e) => {
        const gps = this.extractGpsFromBuffer(e.target.result);
        if (gps && !point._lat && !point._lng) {
          point._lat = gps.lat;
          point._lng = gps.lng;
        }
      };
      arrayReader.readAsArrayBuffer(file);
      // Open cropper
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = async () => {
        URL.revokeObjectURL(url);
        const finalImg = await this.openCropper(img);
        if (!finalImg) return;
        const maxW = 450;
        const w = Math.min(finalImg.width, maxW);
        const h = Math.round(finalImg.height * (w / finalImg.width));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(finalImg, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        point._preview = dataUrl;
        canvas.toBlob(blob => { point._file = blob; }, 'image/jpeg', 0.85);
        point._saved = false;
      };
      img.src = url;
      event.target.value = '';
    },

    removePhoto(point) {
      point._file = null;
      point._preview = null;
      point._saved = false;
    },

    openPhotoEditor(point) {
      if (!point._preview) return;
      this.photoEditor = { open: true, src: point._preview, targetPoint: point };
    },

    onPhotoEditorSave(dataUrl) {
      const point = this.photoEditor.targetPoint;
      point._preview = dataUrl;
      fetch(dataUrl).then(r => r.blob()).then(blob => { point._file = blob; });
      point._saved = false;
      this.photoEditor.open = false;
    },

    openCropper(img) {
      const tmp = document.createElement('canvas');
      tmp.width = img.width; tmp.height = img.height;
      tmp.getContext('2d').drawImage(img, 0, 0);
      const dataUrl = tmp.toDataURL('image/jpeg', 0.95);
      return new Promise(resolve => {
        this.imageCropper = { show: true, src: dataUrl, _resolve: resolve };
      });
    },

    onCropperSave(croppedDataUrl) {
      this.imageCropper.show = false;
      const croppedImg = new Image();
      croppedImg.onload = () => this.imageCropper._resolve(croppedImg);
      croppedImg.src = croppedDataUrl;
    },

    onCropperCancel() {
      this.imageCropper.show = false;
      this.imageCropper._resolve(null);
    },

    extractGpsFromBuffer(buffer) {
      try {
        const view = new DataView(buffer);
        if (view.byteLength < 4 || view.getUint16(0) !== 0xFFD8) return null;
        let offset = 2, tiffStart = -1;
        while (offset + 4 < view.byteLength) {
          const marker = view.getUint16(offset);
          const segLen = view.getUint16(offset + 2);
          if (marker === 0xFFDA) break;
          if (marker === 0xFFE1 && view.getUint8(offset + 4) === 0x45 && view.getUint8(offset + 5) === 0x78 && view.getUint8(offset + 6) === 0x69 && view.getUint8(offset + 7) === 0x66 && view.getUint8(offset + 8) === 0x00 && view.getUint8(offset + 9) === 0x00) { tiffStart = offset + 10; break; }
          offset += 2 + segLen;
        }
        if (tiffStart < 0) return null;
        const bo = view.getUint16(tiffStart), le = bo === 0x4949;
        const r16 = (o) => view.getUint16(tiffStart + o, le);
        const r32 = (o) => view.getUint32(tiffStart + o, le);
        const r8 = (o) => view.getUint8(tiffStart + o);
        if (r16(2) !== 42) return null;
        const ifd0 = r32(4), n0 = r16(ifd0);
        let gpsOff = null;
        for (let i = 0; i < n0; i++) { const e = ifd0 + 2 + i * 12; if (r16(e) === 0x8825) { gpsOff = r32(e + 8); break; } }
        if (gpsOff === null) return null;
        const nGps = r16(gpsOff);
        let latRef = 'N', lngRef = 'E', latRaw = null, lngRaw = null;
        for (let i = 0; i < nGps; i++) {
          const e = gpsOff + 2 + i * 12, tag = r16(e), valOff = r32(e + 8);
          if (tag === 0x0001) { latRef = String.fromCharCode(r8(e + 8)); }
          else if (tag === 0x0003) { lngRef = String.fromCharCode(r8(e + 8)); }
          else if (tag === 0x0002) { latRaw = [r32(valOff) / r32(valOff + 4), r32(valOff + 8) / r32(valOff + 12), r32(valOff + 16) / r32(valOff + 20)]; }
          else if (tag === 0x0004) { lngRaw = [r32(valOff) / r32(valOff + 4), r32(valOff + 8) / r32(valOff + 12), r32(valOff + 16) / r32(valOff + 20)]; }
        }
        if (!latRaw || !lngRaw) return null;
        const toDD = (dms, ref) => { const dd = dms[0] + dms[1] / 60 + dms[2] / 3600; return (ref === 'S' || ref === 'W') ? -dd : dd; };
        return { lat: toDD(latRaw, latRef).toFixed(6), lng: toDD(lngRaw, lngRef).toFixed(6) };
      } catch { return null; }
    },

    async savePoint(point) {
      if (!point._preview || !point._lat || !point._lng) return;
      point._saving = true;
      point._error = '';

      try {
        const db = firebase.database();
        const game = this.gameName;
        const num = point.num;

        const lat = parseFloat(String(point._lat).replace(',', '.'));
        const lng = parseFloat(String(point._lng).replace(',', '.'));

        // Upload image only if a new file was selected (blob present)
        let imageUrl = point._preview; // fallback: keep existing URL
        if (point._file) {
          const storageRef = firebase.storage().ref(game + '/fieldWorker/' + num + '.jpg');
          await storageRef.put(point._file);
          imageUrl = await storageRef.getDownloadURL();
        }

        // Write to fieldWorkerData (source of truth for this page)
        await db.ref(game + '/fieldWorkerData/' + num).set({ imageUrl, lat, lng });

        // Also write to the real game paths so the admin gets the data
        await db.ref(game + '/pointImages/' + num).update({ imageUrl });
        const langsSnap = await db.ref(game + '/languages').once('value');
        const langs = langsSnap.val() || ['he'];
        await Promise.all(langs.map(lang =>
          db.ref(game + '/' + lang + '/points/' + num).update({ lat, lng })
        ));
        await db.ref(game + '/markers/' + num).set({ lat, lng });

        point._saved = true;
        this.showToast('#' + num + ' ' + this.t('fwToastSaved'));
      } catch (err) {
        point._error = this.t('fwSaveError');
        console.error(err);
      }

      point._saving = false;
    },

    openMapPicker(point) {
      this.mapPicker = { open: true, targetPoint: point, selected: null };
      this.$nextTick(() => this.initMapPicker());
    },

    async initMapPicker() {
      const API_KEY = 'AIzaSyACbBFnoaG5EVR7-IDGn8lsiTtPHxWQWB4';
      if (!window.google || !window.google.maps) {
        await new Promise((resolve, reject) => {
          window._mapPickerResolve = resolve;
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&callback=_mapPickerResolve`;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      // Center on current point if already set, else try geolocation, else default Israel
      const pt = this.mapPicker.targetPoint;
      let centerLat = pt._lat ? parseFloat(pt._lat) : null;
      let centerLng = pt._lng ? parseFloat(pt._lng) : null;

      if (!centerLat) { centerLat = this.mapCenter.lat; centerLng = this.mapCenter.lng; }

      const map = new window.google.maps.Map(document.getElementById('fw-map-container'), {
        center: { lat: centerLat, lng: centerLng },
        zoom: this.mapCenter.zoom,
      });

      // Show already-saved points as markers
      this.points.forEach(p => {
        if (!p._lat || !p._lng) return;
        new window.google.maps.Marker({
          position: { lat: parseFloat(p._lat), lng: parseFloat(p._lng) },
          map,
          title: `Point ${p.num}`,
          label: { text: String(p.num), color: 'white', fontSize: '11px', fontWeight: 'bold' },
          icon: p === pt
            ? 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
            : 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        });
      });

      let clickMarker = null;
      map.addListener('click', e => {
        const lat = e.latLng.lat().toFixed(6);
        const lng = e.latLng.lng().toFixed(6);
        this.mapPicker = { ...this.mapPicker, selected: { lat, lng } };
        if (clickMarker) clickMarker.setMap(null);
        clickMarker = new window.google.maps.Marker({ position: e.latLng, map,
          icon: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png' });
      });
    },

    applyMapCoords() {
      if (!this.mapPicker.selected || !this.mapPicker.targetPoint) return;
      const { lat, lng } = this.mapPicker.selected;
      this.mapPicker.targetPoint._lat = lat;
      this.mapPicker.targetPoint._lng = lng;
      this.mapPicker.open = false;
    },

    showToast(text, type = 'success') {
      this.toast = { show: true, text, type };
      setTimeout(() => { this.toast.show = false; }, 3000);
    },
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }

.fw-root {
  min-height: 100vh;
  background: #f0f4f8;
  font-family: system-ui, sans-serif;
  direction: ltr;
}

/* ── Loading / Error ── */
.fw-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 16px;
  padding: 24px;
}
.fw-spinner {
  width: 40px; height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #1a73e8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.fw-muted { color: #94a3b8; font-size: 14px; }
.fw-error-card { text-align: center; padding: 40px 24px; }
.fw-error-card h2 { margin: 12px 0 8px; color: #dc2626; }
.fw-error-card p { color: #64748b; font-size: 14px; }

/* ── Tutorial ── */
.fw-tuto-screen {
  max-width: 560px;
  margin: 0 auto;
  padding: 32px 16px 40px;
}
.fw-tuto-header { text-align: center; margin-bottom: 28px; }
.fw-tuto-emoji { font-size: 48px; }
.fw-tuto-title { font-size: 22px; font-weight: 700; color: #1e293b; margin: 8px 0 4px; }
.fw-tuto-sub { color: #64748b; font-size: 15px; margin: 0; }

.fw-tuto-cards { display: flex; flex-direction: column; gap: 14px; margin-bottom: 28px; }
.fw-tuto-card {
  background: white;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fw-tuto-card-icon { font-size: 32px; }
.fw-tuto-card-title { font-size: 16px; font-weight: 700; color: #1e293b; }
.fw-tuto-card-body { font-size: 14px; color: #475569; line-height: 1.55; }

.fw-start-btn {
  display: block;
  width: 100%;
  padding: 16px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  text-align: center;
}
.fw-start-btn:hover { background: #1557b0; }

/* ── Main ── */
.fw-main { max-width: 560px; margin: 0 auto; padding: 0 0 48px; }

.fw-header {
  background: #1a73e8;
  color: white;
  padding: 16px 20px 14px;
  position: sticky;
  top: 0;
  z-index: 10;
}
.fw-header-title { font-size: 16px; font-weight: 700; margin-bottom: 8px; }
.fw-progress { font-size: 13px; opacity: 0.9; }
.fw-progress-bar {
  margin-top: 6px;
  height: 6px;
  background: rgba(255,255,255,0.3);
  border-radius: 3px;
  overflow: hidden;
}
.fw-progress-fill {
  height: 100%;
  background: white;
  border-radius: 3px;
  transition: width 0.4s ease;
}

.fw-points { display: flex; flex-direction: column; gap: 14px; padding: 16px; }

.fw-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  overflow: hidden;
}
.fw-card--done { border: 2px solid #22c55e; }

.fw-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #f1f5f9;
}
.fw-badge {
  background: #1a73e8;
  color: white;
  font-weight: 700;
  font-size: 15px;
  width: 34px; height: 34px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.fw-done-tag { color: #16a34a; font-weight: 600; font-size: 13px; }
.fw-todo-tag { color: #94a3b8; font-size: 13px; }

.fw-section { padding: 14px 16px 0; }
.fw-section-label { font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 4px; }
.fw-section-hint { font-size: 12px; color: #94a3b8; margin-bottom: 10px; line-height: 1.4; }

.fw-preview-wrap { position: relative; display: inline-block; margin-bottom: 10px; }
.fw-preview-img { width: 100%; max-width: 320px; border-radius: 10px; display: block; }
.fw-remove-btn {
  position: absolute; top: 6px; right: 6px;
  background: rgba(0,0,0,0.55); color: white;
  border: none; border-radius: 50%; width: 28px; height: 28px;
  font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center;
}

.fw-photo-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 4px; }

.fw-upload-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #f1f5f9;
  border: 2px dashed #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}
.fw-upload-label:hover { background: #e2e8f0; }

.fw-edit-photo-btn {
  padding: 9px 16px;
  background: #f1f5f9;
  border: 1.5px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}
.fw-edit-photo-btn:hover { background: #e2e8f0; }

.fw-gps-row { display: flex; gap: 10px; margin-bottom: 10px; }
.fw-input {
  flex: 1;
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
}
.fw-input:focus { outline: none; border-color: #1a73e8; background: white; }

.fw-save-btn {
  display: block;
  width: calc(100% - 32px);
  margin: 14px 16px 16px;
  padding: 13px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}
.fw-save-btn:hover:not(:disabled) { background: #1557b0; }
.fw-save-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.fw-error-msg { color: #dc2626; font-size: 13px; padding: 0 16px 14px; }

.fw-all-done {
  margin: 24px 16px 0;
  background: #dcfce7;
  border: 2px solid #22c55e;
  border-radius: 14px;
  padding: 20px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  color: #15803d;
}

/* ── Toast ── */
.fw-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  z-index: 999;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.fw-toast.success { background: #1a73e8; color: white; }
.fw-toast.error { background: #dc2626; color: white; }

/* ── Map button ── */
.fw-map-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #f1f5f9;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  margin-bottom: 4px;
}
.fw-map-btn:hover { background: #e2e8f0; }

/* ── Map picker modal ── */
.fw-map-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
}
.fw-map-modal {
  width: 100%;
  max-width: 620px;
  background: white;
  border-radius: 18px 18px 0 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.fw-map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
  border-bottom: 1px solid #f1f5f9;
}
.fw-map-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #94a3b8;
  padding: 0 4px;
}
.fw-map-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #f1f5f9;
  gap: 12px;
}
.fw-map-coords { font-size: 13px; color: #1a73e8; font-weight: 600; }
.fw-map-hint-text { font-size: 12px; color: #94a3b8; }
.fw-map-confirm {
  padding: 10px 20px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}
.fw-map-confirm:disabled { opacity: 0.4; cursor: not-allowed; }
.fw-map-confirm:not(:disabled):hover { background: #1557b0; }
</style>
